/**
 * Курс «Наблюдение за искусственной жизнью» (аквариум AI.LAB).
 *
 * Аквариум — статическая страница public/kursy/akvarium/zhizn.html от
 * сессии AI.LAB. В нём нет правил «если улитка — делай так»: поведение
 * выводится из генома. Отзыв людей, которым его показали: «не совсем
 * понятно, что это даёт». Поэтому аквариум здесь не показывают «просто
 * посмотреть»: каждое занятие — опыт. Вопрос, предсказание до запуска,
 * действие, результат, который страница меряет сама по событиям
 * аквариума, сравнение с предсказанием, вывод.
 *
 * Цифры в текстах — из прогонов AI.LAB, это ориентир: каждый аквариум
 * уникален. Формулировки честные: «модель», «обычно».
 */

export const PUT_AKVARIUMA = '/kursy/iskusstvennaya-zhizn';
/** Адрес аквариума во фрейме. */
export const ADRES_AKVARIUMA = '/kursy/akvarium/zhizn.html';

/** Сценарий старта аквариума (?opyt=). */
export type Scenarij = 'privykanie' | 'gorkij' | 'nasledstvo';

export interface Variant {
  id: string;
  tekst: string;
}

export interface Opyt {
  /** Часть адреса и ключ прогресса. Не менять. */
  slug: string;
  scenarij: Scenarij;
  nazvanie: string;
  minut: number;
  title: string;
  description: string;
  h1: string;
  /** Чему учит опыт, для списка опытов. */
  korotko: string;
  vopros: string;
  varianty: Variant[];
  /** Что делать в аквариуме. */
  shagi: string[];
  /** Где смотреть. */
  smotret: string;
  /** Что обычно получается, показывается после опыта. */
  obychno: string;
  vyvod: string[];
  zhivojMir: string;
  mostikII?: string;
}

export const OPYTY: Opyt[] = [
  {
    slug: 'privykanie',
    scenarij: 'privykanie',
    nazvanie: 'Привыкание',
    minut: 5,
    title: 'Привыкание в биологии: опыт с улиткой в аквариуме',
    description:
      'Привыкание — самое простое обучение. Стучим по стеклу и смотрим, как цифровая улитка перестаёт пугаться. Опыт с предсказанием и проверкой.',
    h1: 'Привыкание: будет ли улитка пугаться каждый раз?',
    korotko: 'Самое простое обучение: перестать бояться того, что не опасно.',
    vopros:
      'Если стучать по стеклу рядом с улиткой снова и снова, она будет пугаться так же сильно?',
    varianty: [
      { id: 'tak-zhe', tekst: 'Да, каждый раз одинаково' },
      { id: 'menshe', tekst: 'Нет, будет пугаться всё меньше' },
      { id: 'bolshe', tekst: 'Нет, будет пугаться всё сильнее' },
    ],
    shagi: [
      'Инструмент «стук» уже выбран. Нажмите на воду рядом с одной улиткой.',
      'Стукните рядом с той же улиткой ещё 4 раза подряд, не отходя далеко.',
      'Посмотрите на результат ниже. Если хотите, подождите минуту и стукните снова.',
    ],
    smotret: 'Пугливость улитки и сколько секунд она сидит в раковине после стука.',
    obychno:
      'Пугливость обычно падает со 100% примерно до 25% за пять стуков, а время в укрытии — с 3 до 1,5 секунды. После минуты тишины улитка снова пугливая.',
    vyvod: [
      'В биологии это называют привыканием (габитуацией): животное перестаёт отвечать на раздражитель, который оказался безопасным. Так оно не тратит силы на ложные тревоги.',
      'Привыкание не навсегда: в тишине пугливость возвращается. Это отличает его от усталости.',
    ],
    zhivojMir:
      'Морской моллюск аплизия делает то же самое. Эрик Кандел изучал на нём, как меняются связи между нервными клетками при привыкании, и получил за это Нобелевскую премию в 2000 году.',
  },
  {
    slug: 'gorkij-korm',
    scenarij: 'gorkij',
    nazvanie: 'Горький корм',
    minut: 7,
    title: 'Обучение на последствиях: опыт с горьким кормом',
    description:
      'Сколько проб нужно цифровой креветке, чтобы перестать брать горький корм? Опыт с предсказанием и проверкой в аквариуме прямо в браузере.',
    h1: 'Сколько проб нужно креветке, чтобы научиться?',
    korotko: 'Учиться на последствиях: ожидал вкусного, а оказалось горько.',
    vopros:
      'Один из двух кормов горький. Сколько раз креветке нужно его попробовать, чтобы он ей разонравился?',
    varianty: [
      { id: 'raz', tekst: 'Хватит одного раза' },
      { id: 'neskolko', tekst: '2–3 пробы' },
      { id: 'mnogo', tekst: '10 проб и больше' },
      { id: 'nikogda', tekst: 'Не научится никогда' },
    ],
    shagi: [
      'Бросьте корм рядом с креветками: инструмент «красный корм» уже выбран, синий — рядом.',
      'Бросайте оба цвета по очереди. Над креветкой появится «ням!» или «фу!».',
      'Когда ниже появится результат, нажмите «Поменять, какой горький» и посмотрите, переучится ли креветка.',
    ],
    smotret: 'Насколько креветке нравится каждый корм после очередной пробы.',
    obychno:
      'После 1–3 проб горький корм нравится креветке на 8–21%, вкусный — на 80–92%. Если поменять, какой горький, креветки переучиваются.',
    vyvod: [
      'Креветка ждёт от корма какого-то вкуса. Когда ожидание не сбывается, она меняет своё мнение о корме тем сильнее, чем больше ошиблась.',
      'Поэтому учиться можно и наоборот: когда горьким становится другой корм, ошибка ожидания снова велика, и мнение снова меняется.',
    ],
    zhivojMir:
      'У животных и людей так работает дофамин: он отвечает не на награду, а на разницу между ожиданием и тем, что вышло.',
    mostikII:
      'Обучение с подкреплением в ИИ устроено так же: модель меняет свои оценки на величину ошибки предсказания награды.',
  },
  {
    slug: 'nasledstvo',
    scenarij: 'nasledstvo',
    nazvanie: 'Что передаётся детям',
    minut: 10,
    title: 'Наследуются ли приобретённые признаки: опыт',
    description:
      'Знают ли дети креветки, какой корм горький, если это выучили родители? Проверяем, что передаётся по наследству: знание или способность учиться.',
    h1: 'Знают ли дети то, что выучили родители?',
    korotko: 'Что наследуется: знания или способность их получать.',
    vopros: 'Креветки выучили, что синий корм горький. Будут ли их дети знать это с рождения?',
    varianty: [
      { id: 'znayut', tekst: 'Да, дети сразу знают, какой корм горький' },
      { id: 'sposobnost', tekst: 'Нет, но учиться будут так же хорошо, как родители' },
      { id: 'nichego', tekst: 'Нет, и учиться будут не лучше, чем случайные креветки' },
    ],
    shagi: [
      'Покормите креветок обоими цветами, чтобы они выучили, какой корм горький. Аквариум идёт на скорости ×5.',
      'Кормите дальше: сытые креветки размножаются. Ждите первого малыша, обычно это 2–5 минут.',
      'Результат появится ниже сам, как только родится детёныш у родителей, которые уже учились.',
    ],
    smotret: 'Что знает новорождённый о корме и его «способность учиться» рядом с родительской.',
    obychno:
      'У родителя горький корм нравится примерно на 20%, а у новорождённого — снова на 50%, как у тех, кто ещё не пробовал. Способность учиться у ребёнка почти такая же, как у родителей.',
    vyvod: [
      'Ламарк считал, что приобретённые признаки наследуются. Аквариум показывает обратное: выученное за жизнь детям не передаётся.',
      'Передаётся способность учиться — она записана в геноме. Если корм часто меняется, выживают те, кто быстрее учится, и способность растёт из поколения в поколение. Это уже Дарвин.',
    ],
    zhivojMir:
      'Дети не рождаются со знаниями родителей, но рождаются с мозгом, который умеет учиться, — и он достался им по наследству.',
    mostikII:
      'В ИИ похоже: веса, выученные моделью, не появляются у новой модели сами. Передаются архитектура и способ обучения.',
  },
];

export function najtiOpyt(slug: string): Opyt | undefined {
  return OPYTY.find((o) => o.slug === slug);
}

export function putOpyta(o: Opyt): string {
  return `${PUT_AKVARIUMA}/${o.slug}`;
}

// ---------- События аквариума и проверка опыта ----------

type Cvet = 'red' | 'blue';
interface Nravitsya {
  red: number;
  blue: number;
}

export type Sobytie =
  | { tip: 'start'; t: number; gorkij: Cvet }
  | {
      tip: 'stuk';
      t: number;
      blizhajshie: {
        id: number;
        vid?: string;
        puglivostDo: number;
        puglivostPosle: number;
        vUkrytii: number;
      }[];
    }
  | { tip: 'gorkij_smenen'; t: number; gorkij: Cvet }
  | {
      tip: 'proba_korma';
      t: number;
      id: number;
      vid?: string;
      cvet: Cvet;
      gorkij: boolean;
      nravitsya: Nravitsya;
      probaNomer: number;
    }
  | {
      tip: 'rozhdenie';
      t: number;
      id: number;
      vid?: string;
      sposobnostUchitsya: number;
      roditeliSposobnost: number[];
      roditeliNravitsya: Nravitsya[];
      nravitsya: Nravitsya;
    }
  | { tip: 'drugoe'; t: number };

/** Сообщение из фрейма → событие или null, если это не аквариум. */
export function razobratSoobshchenie(d: unknown): Sobytie | null {
  if (typeof d !== 'object' || d === null) return null;
  const o = d as Record<string, unknown>;
  if (o.akvarium !== 1 || typeof o.tip !== 'string') return null;
  const t = typeof o.t === 'number' ? o.t : 0;
  switch (o.tip) {
    case 'start':
    case 'stuk':
    case 'gorkij_smenen':
    case 'proba_korma':
    case 'rozhdenie':
      return { ...(o as object), tip: o.tip, t } as Sobytie;
    default:
      return { tip: 'drugoe', t };
  }
}

export interface Itog {
  /** Опыт сделан: можно сравнить с предсказанием. */
  gotovo: boolean;
  /** Что сейчас сделано, одной строкой: «стуков рядом с улиткой: 3 из 5». */
  hod: string;
  /** Что получилось, понятными словами; пусто, пока опыт не сделан. */
  rezultat: string[];
  /** Какой вариант ответа подтвердился. */
  verno?: string;
}

const proc = (x: number) => `${String(Math.round(x * 100))}%`;
const sek = (x: number) => `${x.toFixed(1).replace('.', ',')} с`;

/** Привыкание: пять стуков рядом с одной улиткой. */
export function itogPrivykaniya(sob: Sobytie[]): Itog {
  const stuki = sob.filter((s): s is Extract<Sobytie, { tip: 'stuk' }> => s.tip === 'stuk');
  // Улитка, рядом с которой стучали чаще всего.
  const poId = new Map<number, { puglivost: number; ukrytie: number; t: number }[]>();
  for (const s of stuki) {
    const b = s.blizhajshie[0];
    if (!b) continue;
    const r = poId.get(b.id) ?? [];
    r.push({ puglivost: b.puglivostDo, ukrytie: b.vUkrytii, t: s.t });
    poId.set(b.id, r);
  }
  const ryad = [...poId.values()].sort((a, b) => b.length - a.length)[0] ?? [];
  const n = ryad.length;
  if (n < 5) {
    return { gotovo: false, hod: `Стуков рядом с одной улиткой: ${String(n)} из 5`, rezultat: [] };
  }
  const pervyj = ryad[0];
  const pyatyj = ryad[4];
  if (!pervyj || !pyatyj) return { gotovo: false, hod: '', rezultat: [] };
  const rezultat = [
    `Пугливость улитки: ${proc(pervyj.puglivost)} при первом стуке → ${proc(pyatyj.puglivost)} при пятом.`,
    `В раковине после стука: ${sek(pervyj.ukrytie)} → ${sek(pyatyj.ukrytie)}.`,
  ];
  // Вернулась ли пугливость после паузы не меньше минуты.
  for (let i = 5; i < n; i++) {
    const tek = ryad[i];
    const pred = ryad[i - 1];
    if (tek && pred && tek.t - pred.t >= 60) {
      rezultat.push(
        `После паузы ${String(Math.round(tek.t - pred.t))} с пугливость снова ${proc(tek.puglivost)}: в тишине привыкание проходит.`,
      );
      break;
    }
  }
  const verno =
    pyatyj.puglivost < pervyj.puglivost * 0.8
      ? 'menshe'
      : pyatyj.puglivost > pervyj.puglivost * 1.2
        ? 'bolshe'
        : 'tak-zhe';
  return { gotovo: true, hod: 'Пять стуков сделано', rezultat, verno };
}

/** Горький корм: креветка перестала любить горький — сколько проб это заняло. */
export function itogGorkogo(sob: Sobytie[]): Itog {
  let gorkij: Cvet | null = null;
  let posleSmeny = false;
  const proby = new Map<
    number,
    { vid?: string; cvet: Cvet; gorkij: boolean; nravitsya: Nravitsya; smena: boolean }[]
  >();
  for (const s of sob) {
    if (s.tip === 'start') gorkij = s.gorkij;
    if (s.tip === 'gorkij_smenen') {
      gorkij = s.gorkij;
      posleSmeny = true;
    }
    // Корм пробуют и креветки, и улитки: считаем любого, кто пробовал.
    if (s.tip === 'proba_korma') {
      const r = proby.get(s.id) ?? [];
      r.push({
        vid: s.vid,
        cvet: s.cvet,
        gorkij: s.gorkij,
        nravitsya: s.nravitsya,
        smena: posleSmeny,
      });
      proby.set(s.id, r);
    }
  }
  // Первая креветка, у которой горький разонравился (меньше 30%).
  for (const ryad of proby.values()) {
    const doSmeny = ryad.filter((p) => !p.smena);
    let gorkihProb = 0;
    for (const p of doSmeny) {
      if (p.gorkij) gorkihProb++;
      const g = p.gorkij ? p.cvet : p.cvet === 'red' ? 'blue' : 'red';
      if (gorkihProb > 0 && p.nravitsya[g] < 0.3) {
        const vkusnyj: Cvet = g === 'red' ? 'blue' : 'red';
        const nazv = (c: Cvet) => (c === 'red' ? 'красный' : 'синий');
        const rezultat = [
          `${p.vid ? `«${p.vid}»` : 'Существу'}: хватило горьких проб — ${String(gorkihProb)}. Теперь ${nazv(g)} корм ей нравится на ${proc(p.nravitsya[g])}, ${nazv(vkusnyj)} — на ${proc(p.nravitsya[vkusnyj])}.`,
        ];
        const posle = ryad.filter((q) => q.smena);
        const poslednyaya = posle[posle.length - 1];
        if (poslednyaya && gorkij) {
          rezultat.push(
            `После смены горького ${nazv(gorkij)} корм нравится ему уже на ${proc(poslednyaya.nravitsya[gorkij])}: существо переучивается.`,
          );
        }
        const verno = gorkihProb === 1 ? 'raz' : gorkihProb <= 3 ? 'neskolko' : 'mnogo';
        return { gotovo: true, hod: 'Научилось первое существо', rezultat, verno };
      }
    }
  }
  const vsegoProb = [...proby.values()].reduce((s, r) => s + r.length, 0);
  return {
    gotovo: false,
    hod:
      vsegoProb === 0
        ? 'Корм ещё никто не попробовал'
        : `Проб корма: ${String(vsegoProb)}, пока никто не научился`,
    rezultat: [],
  };
}

/** Что передаётся детям: первый детёныш у родителей, которые уже учились. */
export function itogNasledstva(sob: Sobytie[]): Itog {
  let gorkij: Cvet = 'blue';
  let rodilos = 0;
  for (const s of sob) {
    if (s.tip === 'start' || s.tip === 'gorkij_smenen') gorkij = s.gorkij;
    if (s.tip !== 'rozhdenie') continue;
    rodilos++;
    const uchilsya = s.roditeliNravitsya.some((r) => r[gorkij] < 0.4);
    if (!uchilsya) continue;
    const rod = s.roditeliNravitsya.map((r) => proc(r[gorkij])).join(' и ');
    const sp = s.roditeliSposobnost.map((x) => x.toFixed(2).replace('.', ',')).join(' и ');
    const rezultat = [
      `Родителям горький корм нравился на ${rod}. Новорождённому — на ${proc(s.nravitsya[gorkij])}, как будто он никогда его не пробовал.`,
      `Способность учиться: у родителей ${sp}, у ребёнка ${s.sposobnostUchitsya.toFixed(2).replace('.', ',')}.`,
    ];
    const blizko = s.roditeliSposobnost.some((x) => Math.abs(x - s.sposobnostUchitsya) < 0.15);
    const verno = s.nravitsya[gorkij] < 0.4 ? 'znayut' : blizko ? 'sposobnost' : 'nichego';
    return { gotovo: true, hod: 'Родился детёныш у учившихся родителей', rezultat, verno };
  }
  return {
    gotovo: false,
    hod:
      rodilos === 0
        ? 'Пока никто не родился. Кормите креветок обоими цветами'
        : `Родилось ${String(rodilos)}, но у родителей, которые ещё не пробовали горького. Кормите дальше`,
    rezultat: [],
  };
}

export function itogOpyta(o: Opyt, sob: Sobytie[]): Itog {
  switch (o.scenarij) {
    case 'privykanie':
      return itogPrivykaniya(sob);
    case 'gorkij':
      return itogGorkogo(sob);
    case 'nasledstvo':
      return itogNasledstva(sob);
  }
}

export const TEKST_AKVARIUMA = {
  // Title и description — Seo-marketing 07.10 (живые запросы Яндекса:
  // «симулятор эволюции», «виртуальная лаборатория биология»).
  title: 'Искусственная жизнь: симулятор эволюции и опыты онлайн',
  description:
    'Виртуальная лаборатория в браузере: аквариум, где поведение существ растёт из генома, а не из правил. Опыты про привыкание, обучение и наследственность.',
  h1: 'Наблюдение за искусственной жизнью',
  lead: 'В этом аквариуме нет ни одного правила «если улитка — делай так». У каждого существа есть геном — набор чисел, — а ещё простая физика и еда. Испуг, обучение, конкуренция и новые формы получаются сами. Поэтому ответ на вопрос опыта заранее не знает даже автор аквариума.',
  kakIdet: [
    'Каждый опыт начинается с вопроса. До запуска вы записываете своё предсказание.',
    'Затем делаете в аквариуме то, что сказано в задании, а страница сама меряет результат.',
    'В конце видно, совпало ли предсказание, что это значит и где то же самое встречается в живой природе и в ИИ.',
  ],
  /** Статьи AI.LAB в блоге по той же теме (перелинковка, Seo-marketing 07.10). */
  stati: [
    {
      href: '/blog/cifrovoj-mozg-muhi',
      nazvanie: 'Мы запустили цифровой мозг мухи на домашней видеокарте',
    },
    {
      href: '/blog/ekonomnyj-mozg-uchitsya',
      nazvanie: 'Почему экономный мозг учится лучше: опыт на цифровой мухе',
    },
    {
      href: '/blog/evolyuciya-obmanyvaet-metriku',
      nazvanie: 'Эволюция обманывает метрику: пять лазеек нашего цифрового организма',
    },
  ],
  predely: [
    'Это модель, а не настоящие животные: «мозг» существа — несколько связей, а не нейроны.',
    'Баланс аквариума подобран вручную и не всегда устойчив.',
    'Каждый аквариум уникален: цифры в описаниях — то, что обычно получается, а не правильный ответ.',
  ],
} as const;

/** «3 опыта», «5 опытов», «1 опыт». */
export function skolkoOpytov(n: number): string {
  const d = n % 10;
  const s = n % 100;
  const slovo =
    d === 1 && s !== 11 ? 'опыт' : d >= 2 && d <= 4 && (s < 12 || s > 14) ? 'опыта' : 'опытов';
  return `${String(n)} ${slovo}`;
}
